import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Button,
  Row,
  Alert,
} from "reactstrap";
import { P } from "../../../AbstractElements";
import { Service, ServiceStatus } from "../../../Types/ServiceManagement";
import { usePayments } from "../../../hooks/usePayment";
import AlertModal from "./AlertModal"; // Import the new AlertModal component
import { useServiceRequest } from "../../../hooks/useServiceRequest";
import { format } from "date-fns";

type Props = {
  service: Service;
};

function SericeByIdContainer({ service }: Props) {
  const { sendPaymentLink, loading, error } = usePayments();
  const { updateRequestStatus, serviceRequestLoading, serviceError } =
    useServiceRequest();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertAction, setAlertAction] = useState<() => void>(() => {});

  const [showReasonInput, setShowReasonInput] = useState(false);

  const toggleAlert = () => {
    setIsAlertOpen(false);
    setShowReasonInput(false);
  };

  const showAlert = (
    message: string,
    action: (reason?: string) => void,
    requireReason = false
  ) => {
    setAlertMessage(message);
    setAlertAction(() => action);
    setShowReasonInput(requireReason);
    setIsAlertOpen(true);
  };

  const handleSendPaymentLink = async () => {
    showAlert("Are you sure you want to send the payment link?", async () => {
      try {
        const result = await sendPaymentLink(service.id);
        if (result) {
          setSuccessMessage("Payment link sent successfully!");
          window.location.reload();
        }
      } catch (err) {
        console.error("Error sending payment link:", err);
      } finally {
        toggleAlert();
      }
    });
  };

  const denyRequest = () => {
    showAlert(
      "Are you sure you want to deny this request?",
      async (reason?: string) => {
        try {
          await updateRequestStatus(ServiceStatus.DENIED, service.id, reason);
          window.location.reload();
        } catch (error) {
          console.error("Error denying request:", error);
        } finally {
          toggleAlert();
        }
      },
      true
    );
  };

  const acceptPayment = () => {
    showAlert("Are you sure you want to accept this payment?", async () => {
      try {
        await updateRequestStatus(ServiceStatus.ACCEPT, service.id);
        window.location.reload();
      } catch (error) {
        console.error("Error sending payment link:", error);
      } finally {
        toggleAlert();
      }
    });
  };

  const denyPayment = () => {
    showAlert(
      "Are you sure you want to deny this payment?",
      async (reason?: string) => {
        try {
          await updateRequestStatus(
            ServiceStatus.PAYMENT_DENIED,
            service.id,
            reason
          );
          window.location.reload();
        } catch (error) {
          console.error("Error sending payment link:", error);
        } finally {
          toggleAlert();
        }
      },
      true
    );
  };

  const visitCanceled = () => {
    showAlert(
      "Are you sure you want to cancel this visit?",
      async (reason?: string) => {
        try {
          await updateRequestStatus(
            ServiceStatus.CANCELLED,
            service.id,
            reason
          );
          window.location.reload();
        } catch (error) {
          console.error("Error sending payment link:", error);
        } finally {
          toggleAlert();
        }
      },
      true
    );
  };

  const serviceCompleted = () => {
    showAlert(
      "Are you sure you want to mark this service as completed?",
      async () => {
        try {
          await updateRequestStatus(ServiceStatus.COMPLETED, service.id);
          window.location.reload();
        } catch (error) {
          console.error("Error sending payment link:", error);
        } finally {
          toggleAlert();
        }
      }
    );
  };

  return (
    <Col>
      <Row>
        <Form>
          <Card>
            <CardBody>
              <Row>
                <Col md={12} className="mb-3">
                  <P className={"card-title mb-0"}>Request Management</P>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>{"Request Id"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={service?.id}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>{"Customer Name"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={`${service.firstName} ${service.lastName}`}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>{"Request Status"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={service.status}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Customer Contact Number"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={service.contactNumber}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Customer Email"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={service.email}
                      disabled
                    />
                  </FormGroup>
                </Col>

                <h5 style={{ marginTop: 40 }}>Address</h5>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Unit Number"}</Label>
                    <Input type="text" value={service.unitNumber} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Street Number"}</Label>
                    <Input type="text" value={service.streetNumber} disabled />
                  </FormGroup>
                </Col>

                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Street Name"}</Label>
                    <Input type="text" value={service.streetName} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Suburb"}</Label>
                    <Input type="text" value={service.suburb} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"VIC Postal Code"}</Label>
                    <Input type="text" value={service.vicPostalCode} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6" style={{ marginBottom: 40 }}>
                  <FormGroup>
                    <Label>{"State"}</Label>
                    <Input type="text" value={service.state} disabled />
                  </FormGroup>
                </Col>

                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Service Name"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={`${service.serviceType?.name}`}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Service Requested Date"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={format(new Date(service.createdAt), "yyyy/MM/dd")}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Service Preferred Date"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={format(
                        new Date(service.serviceDate),
                        "yyyy/MM/dd"
                      )}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Service Preferred Time"}</Label>
                    <Input
                      type="text"
                      placeholder="Company"
                      value={service.serviceTime}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col sm={12}>
                  <FormGroup>
                    <Label>{"Message"}</Label>
                    <Input
                      type="textarea"
                      placeholder="No Message"
                      value={service.message}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>
      </Row>
      {error && <Alert color="danger">{error}</Alert>}
      {serviceError && <Alert color="danger">{serviceError}</Alert>}
      {successMessage && <Alert color="success">{successMessage}</Alert>}
      {service.status === ServiceStatus.NEW && (
        <Row>
          <Col sm="6" md="6">
            <Button disabled={loading} onClick={handleSendPaymentLink}>
              Send Payment Link
            </Button>
          </Col>
          <Col sm="6" md="6">
            <Button disabled={serviceRequestLoading} onClick={denyRequest}>
              Deny
            </Button>
          </Col>
        </Row>
      )}
      {service.status === ServiceStatus.SENT && (
        <Row>
          <Col sm="6" md="6">
            <Button disabled={serviceRequestLoading} onClick={acceptPayment}>
              Payment Accept
            </Button>
          </Col>
          <Col sm="6" md="6">
            <Button disabled={serviceRequestLoading} onClick={denyPayment}>
              Deny
            </Button>
          </Col>
        </Row>
      )}
      {service.status === ServiceStatus.ACCEPT && (
        <Row>
          <Col sm="6" md="6">
            <Button disabled={serviceRequestLoading} onClick={serviceCompleted}>
              Service Complete
            </Button>
          </Col>
          <Col sm="6" md="6">
            <Button disabled={serviceRequestLoading} onClick={visitCanceled}>
              Cancelled
            </Button>
          </Col>
        </Row>
      )}
      <AlertModal
        isOpen={isAlertOpen}
        toggle={toggleAlert}
        message={alertMessage}
        onConfirm={alertAction}
        onCancel={toggleAlert}
        showReasonInput={showReasonInput}
      />
    </Col>
  );
}

export default SericeByIdContainer;
